import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { getLeagueTeamsList } from '../api/league';
import { getUniverses } from '../redux/actions/SignUp';
import { getLoadingUniversesData } from '../redux/selector/SignUp';
import { getMyCurrentTeam, getUser } from '../redux/selector/User';
import { calendarData, leagueTable } from '../redux/selector/League';
import { compareUniverseNames } from '../service/clubServer';

const normalizeTeams = (data, leagueName, myTeamId) => {
  const list = Array.isArray(data) ? data : data?.teams || [];
  return list
    .filter((team) => team && team._id && team._id !== myTeamId)
    .map((team) => ({
      ...team,
      league: leagueName,
    }));
};

const addCrests = (map, list) => {
  (list || []).forEach((team) => {
    if (team?._id && team.symbol) {
      map[String(team._id)] = team.symbol;
    }
  });
};

const useInviteServerFilter = ({ inviteTeams, fallbackLeagues }) => {
  const dispatch = useDispatch();
  const { universesList } = useSelector(getLoadingUniversesData);
  const { leagueName } = useSelector(getUser);
  const myTeam = useSelector(getMyCurrentTeam);
  const myTeamId = myTeam?._id;
  const tableTeams = useSelector(leagueTable);
  const calendar = useSelector(calendarData);
  const teams = Array.isArray(inviteTeams)
    ? inviteTeams
    : Array.isArray(inviteTeams?.teams)
      ? inviteTeams.teams
      : [];

  const [selectedUniverseName, setSelectedUniverseName] = useState(null);
  const [remoteTeamsByLeague, setRemoteTeamsByLeague] = useState({});
  const [remoteStatus, setRemoteStatus] = useState('idle');
  const [fetchedCrests, setFetchedCrests] = useState({});

  useEffect(() => {
    if (!universesList?.length) {
      dispatch(getUniverses(false));
    }
  }, [dispatch, universesList?.length]);

  const homeUniverse = useMemo(
    () =>
      (universesList || []).find((universe) =>
        (universe.leagues || []).some((league) => league.code === leagueName)
      ),
    [universesList, leagueName]
  );

  useEffect(() => {
    if (homeUniverse?.name && !selectedUniverseName) {
      setSelectedUniverseName(homeUniverse.name);
    }
  }, [homeUniverse, selectedUniverseName]);

  const selectedUniverse = useMemo(
    () =>
      (universesList || []).find(
        (universe) => universe.name === selectedUniverseName
      ) || homeUniverse,
    [universesList, selectedUniverseName, homeUniverse]
  );

  const isHomeServer =
    !selectedUniverse || selectedUniverse.name === homeUniverse?.name;

  const leagues = useMemo(() => {
    if (isHomeServer && !isEmpty(fallbackLeagues)) {
      return fallbackLeagues;
    }
    return selectedUniverse?.leagues || fallbackLeagues || [];
  }, [isHomeServer, fallbackLeagues, selectedUniverse]);

  useEffect(() => {
    if (isHomeServer || !selectedUniverse) {
      setRemoteTeamsByLeague({});
      setRemoteStatus('idle');
      return undefined;
    }

    let cancelled = false;
    const loadRemoteTeams = async () => {
      setRemoteStatus('loading');
      const next = {};
      let any = false;
      await Promise.all(
        (selectedUniverse.leagues || []).map(async (league) => {
          try {
            const response = await getLeagueTeamsList(league._id);
            if (response.status === 200) {
              const data = await response.json();
              const normalized = normalizeTeams(data, league.name, myTeamId);
              next[league._id] = normalized;
              if (normalized.length) any = true;
            } else {
              next[league._id] = [];
            }
          } catch (error) {
            next[league._id] = [];
          }
        })
      );
      if (!cancelled) {
        setRemoteTeamsByLeague(next);
        setRemoteStatus(any ? 'ok' : 'empty');
      }
    };

    loadRemoteTeams();
    return () => {
      cancelled = true;
    };
  }, [isHomeServer, selectedUniverse, myTeamId]);

  useEffect(() => {
    if (!isHomeServer || isEmpty(leagues) || teams.length === 0) {
      return undefined;
    }
    if (!teams.some((team) => team?._id && !team.symbol)) {
      return undefined;
    }

    let cancelled = false;
    const loadCrests = async () => {
      const map = {};
      await Promise.all(
        leagues.map(async (league) => {
          if (!league?._id) return;
          try {
            const response = await getLeagueTeamsList(league._id);
            if (response.status === 200) {
              const data = await response.json();
              addCrests(map, Array.isArray(data) ? data : data?.teams);
            }
          } catch (error) {
            /* keep names without crests */
          }
        })
      );
      if (!cancelled) {
        setFetchedCrests(map);
      }
    };

    loadCrests();
    return () => {
      cancelled = true;
    };
  }, [isHomeServer, leagues, teams]);

  const getTeamsForLeague = (league) => {
    if (isHomeServer) {
      return teams.filter((team) => team.league === league.name);
    }
    return remoteTeamsByLeague[league._id] || [];
  };

  const crestByTeamId = useMemo(() => {
    const map = { ...fetchedCrests };
    addCrests(map, teams);
    addCrests(map, tableTeams);
    Object.values(remoteTeamsByLeague).forEach((list) => addCrests(map, list));
    const days = Array.isArray(calendar?.matches) ? calendar.matches : [];
    days.forEach((day) => {
      (day?.matches || []).forEach((match) => {
        if (match?.home_team?._id && match.home_team.symbol) {
          map[String(match.home_team._id)] = match.home_team.symbol;
        }
        if (match?.away_team?._id && match.away_team.symbol) {
          map[String(match.away_team._id)] = match.away_team.symbol;
        }
      });
    });
    return map;
  }, [fetchedCrests, teams, tableTeams, remoteTeamsByLeague, calendar]);

  const universeOptions = useMemo(
    () =>
      [...(universesList || [])]
        .map((universe) => ({
          value: universe.name,
          label: universe.name,
        }))
        .sort((a, b) => compareUniverseNames(a.label, b.label)),
    [universesList]
  );

  return {
    universeOptions,
    selectedUniverseName: selectedUniverse?.name || selectedUniverseName,
    setSelectedUniverseName,
    leagues,
    isHomeServer,
    remoteStatus,
    getTeamsForLeague,
    crestByTeamId,
  };
};

export default useInviteServerFilter;
